import { Box, Chip, useTheme } from "@mui/material";
import { useCallback, useMemo } from "react";
import {
  AutocompleteInput,
  BooleanInput,
  ChipField,
  Datagrid,
  EditButton,
  List,
  NumberInput,
  RaRecord,
  ReferenceArrayField,
  ReferenceInput,
  SearchInput,
  SelectField,
  ShowButton,
  SingleFieldList,
  SortPayload,
  TextField,
  TextInput,
  useRecordContext,
} from "react-admin";
import CustomCountryChip from "shared/components/chips/CustomCountryChip";
import { GenericColorChip } from "shared/components/chips/GenericColorChip";
import CustomBooleanField from "shared/components/fields/CustomBooleanField";
import CustomDateTimeInput from "shared/components/inputs/CustomDateTimeInput";
import InputContainer from "shared/components/inputs/InputContainer";
import {
  dsoColorMapping,
  siteModeColorMapping,
  siteOperatorColorMapping,
} from "shared/enums/colorMappings/globalColorMappings";
import {
  allDsoChoices,
  siteCountryChoices,
  siteModeChoices,
  siteOperatorChoices,
} from "shared/enums/reactAdminChoices";
import { SiteResourcesEnum } from "shared/enums/resourceMappings";
import { isSitePendingSynchronization } from "../shared/utils";

const SiteRowExpand = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box display={{ xs: "block", sm: "flex" }}>
      <InputContainer source="zipcode" />
      <InputContainer source="efficiency" />
      <InputContainer source="aggregator_module_healthcheck_url" />
      <InputContainer source="network_ip" />
      <InputContainer source="mode" Field={SelectField} fieldCustomProps={{ choices: siteModeChoices }} />
      <InputContainer source="useful_energy_at_1mw" />
    </Box>
  );
};

export const SiteList = () => {
  const filters = useMemo(
    () => [
      <SearchInput source="search" key={"search"} alwaysOn autoFocus />,
      <ReferenceInput
        source="id__in"
        key="id__in"
        reference={SiteResourcesEnum.Groups}
        filter={{ type: "rte_edr" }}
        label="EDR Selection"
        alwaysOn
        perPage={100}
      >
        <AutocompleteInput
          multiple
          optionValue="site_ids"
          label="EDR Selection"
          parse={(selectedGroupIds) => {
            if (!selectedGroupIds || !selectedGroupIds.length || !Array.isArray(selectedGroupIds)) return undefined;
            return selectedGroupIds;
          }}
          format={(value) => (Array.isArray(value) ? value : [])}
          sx={{ minWidth: 300 }}
        />
      </ReferenceInput>,

      <ReferenceInput source="name__ilike" key="name__ilike" label="Site" reference={SiteResourcesEnum.Sites}>
        <AutocompleteInput optionText="name" label="Site" />
      </ReferenceInput>,

      <TextInput source="city" key="city" label="City" />,
      <AutocompleteInput source="country" key="country" label="Country" choices={siteCountryChoices} />,
      <TextInput source="zipcode" key="zipcode" label="Zip Code" />,
      <NumberInput source="max_cycles" key="max_cycles" label="Max Cycles" />,
      <NumberInput source="max_cycles__lt" key="max_cycles__lt" label="Max Cycles (Less Than)" />,
      <NumberInput source="max_cycles__gt" key="max_cycles__gt" label="Max Cycles (Greater Than)" />,
      <TextInput
        source="aggregator_module_healthcheck_url"
        key="aggregator_module_healthcheck_url"
        label="Healthcheck URL"
      />,
      <TextInput source="network_ip" key="network_ip" label="Network IP" />,
      <AutocompleteInput source="dso" key="dso" label="DSO" choices={allDsoChoices} />,
      <TextInput
        source="asset_id__in"
        key="asset_id__in"
        label="Asset IDs (Separate by commas)"
        alwaysOn
        parse={(v) => {
          const splitElements = v ? v.split(",") : [];
          return splitElements.map((s: string) => s.trim());
        }}
        format={(v) => (Array.isArray(v) ? v.join(",") : "")}
        sx={{ minWidth: 300 }}
      />,
      <TextInput source="rte_code" key="rte_code" label="RTE Code" />,
      <NumberInput source="rack_count" key="rack_count" label="Rack Count" />,
      <NumberInput source="min_power_mw" key="min_power_mw" label="Min Power (MW)" />,
      <NumberInput source="max_power_mw" key="max_power_mw" label="Max Power (MW)" />,
      <NumberInput source="efficiency" key="efficiency" label="Efficiency" />,
      <NumberInput source="latitude" key="latitude" label="Latitude" />,
      <NumberInput source="longitude" key="longitude" label="Longitude" />,
      <NumberInput source="max_soc_mwh" key="max_soc_mwh" label="Max SOC (MWh)" />,
      <NumberInput source="soh" key="soh" label="SOH" />,
      <NumberInput source="useful_energy_at_1mw" key="useful_energy_at_1mw" label="Useful Energy at 1MW" />,
      <NumberInput source="soc_inf" key="soc_inf" label="SOC Inf" />,
      <NumberInput source="soc_sup" key="soc_sup" label="SOC Sup" />,
      <NumberInput source="soc_reserve_inf" key="soc_reserve_inf" label="SOC Reserve Inf" />,
      <NumberInput source="soc_reserve_sup" key="soc_reserve_sup" label="SOC Reserve Sup" />,
      <TextInput source="prm" key="prm" label="PRM" />,
      <AutocompleteInput
        source="site_operator"
        key="site_operator"
        label="Site Operator"
        choices={siteOperatorChoices}
      />,
      <CustomDateTimeInput source="created_at" key="created_at" label="Created At" />,
      <TextInput source="supplier" key="supplier" label="Supplier" />,
      <BooleanInput source="iecharge" key="iecharge" label="IE Charge" />,
      <NumberInput source="rte_fcr_computation_version" key="rte_fcr_computation_version" label="RTE FCR Version" />,
    ],
    [],
  );
  return (
    <List sort={{ field: "id", order: "ASC" } as SortPayload} filters={filters} perPage={50}>
      <Datagrid
        rowClick="expand"
        expand={<SiteRowExpand />}
        bulkActionButtons={false}
        sx={{
          "& .RaDatagrid-expandedPanel": {
            backgroundColor: "lightGrey",
          },
        }}
      >
        <TextField source="id" />
        <TextField source="asset_id" label="Asset ID" />
        <TextField source="name" />
        <ReferenceArrayField source="group_ids" reference={SiteResourcesEnum.Groups} sortable={false}>
          <SingleFieldList linkType="show">
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
        <CustomCountryChip source="country" />
        <GenericColorChip source="dso" colorMapping={dsoColorMapping} sortable={false} />
        <GenericColorChip source="mode" colorMapping={siteModeColorMapping} sortable={false} />
        <CustomBooleanField source="iecharge" label="IECharge" sortable={false} />
        <TextField source="rte_code" label={"Tranche TM site"} sortable={false} />
        <GenericColorChip source="site_operator" colorMapping={siteOperatorColorMapping} sortable={false} />
        <StatusField source="configuration_status" sortable={false} />

        <EditButton />
        <ShowButton />
      </Datagrid>
    </List>
  );
};

const StatusField = ({ source, sortable = false }: { source: string; sortable?: boolean }) => {
  const record = useRecordContext({ source, sortable });
  const theme = useTheme();

  const getTagInfos = useCallback(
    (currentSite: RaRecord) => {
      if (isSitePendingSynchronization(currentSite)) {
        return { color: theme.palette.warning.main, text: "Pending Synchronization" };
      }
      return { color: theme.palette.success.main, text: "Synchronized" };
    },
    [theme.palette.success.main, theme.palette.warning.main],
  );

  const tagInfos = record ? getTagInfos(record) : { color: theme.palette.error.main, text: "No Record" };

  return record ? <Chip label={tagInfos.text} sx={{ bgcolor: tagInfos.color, color: "white" }} /> : null;
};

export default StatusField;
